import { gql, useLazyQuery } from '@apollo/client';
import {
  ConversationParticipantEntity,
  ConversationParticipantOption,
  GetConversationParticipantOptionsInput,
} from '@provision/conversation-types';
import { useCallback, useMemo } from 'react';
import { uniqueBy } from 'remeda';
import { Records } from 'src/graphql/types';
import { META_DATA_QUERY_FIELDS } from './metadata.fragments';

export interface PreparedConversationParticipantOption {
  id: string;
  value: string;
  label: string;
  group: string;
  participantEntity: ConversationParticipantEntity;
  conversationParticipantId: string;
  phoneNumber: string;
  email: string;
  entityPartnerId?: number;
  avatarFileKey: string | null;
}

interface GetConversationParticipantOptionsQuery {
  getConversationParticipantOptions: Records<ConversationParticipantOption>;
}

export const CONVERSATION_PARTICIPANT_OPTION_FIELDS = gql`
  fragment ConversationParticipantOptionField on ConversationParticipantOption {
    id
    conversationParticipantId
    participantEntity
    participantIdentity
    firstName
    lastName
    phoneNumber
    email
    entityPartnerId
    avatarFileKey
  }
`;

const GET_CONVERSATION_PARTICIPANT_OPTIONS_QUERY = gql`
  query GetConversationParticipantOptions(
    $partnerId: Int!
    $searchText: String
    $skipClients: Boolean
    $skipPlatformUsers: Boolean
    $skipTotalRecords: Boolean = false
  ) @entity(name: "ConversationParticipantOption") {
    getConversationParticipantOptions(
      input: {
        partnerId: $partnerId
        searchText: $searchText
        skipClients: $skipClients
        skipPlatformUsers: $skipPlatformUsers
      }
    ) {
      records {
        ...ConversationParticipantOptionField
      }
      metadata {
        ...MetaDataQueryFields
      }
    }
  }
  ${CONVERSATION_PARTICIPANT_OPTION_FIELDS}
  ${META_DATA_QUERY_FIELDS}
`;

const useGetConversationParticipantOptions = () => {
  const [fetchConversationParticipantOptions, { previousData, data, loading }] =
    useLazyQuery<GetConversationParticipantOptionsQuery>(
      GET_CONVERSATION_PARTICIPANT_OPTIONS_QUERY,
      {
        // Caching is disabled so we don't hit the stale data after a client data is changed
        fetchPolicy: 'network-only',
        context: { includePlatformLevelData: true, ttl: 0 },
      },
    );

  const getConversationParticipantOptions = useCallback(
    (input: GetConversationParticipantOptionsInput, allowEmpty = false) => {
      if (!allowEmpty && !input.searchText) return;
      void fetchConversationParticipantOptions({ variables: input });
    },
    [fetchConversationParticipantOptions],
  );

  const conversationParticipantOptions = useMemo<ConversationParticipantOption[]>(
    () =>
      data?.getConversationParticipantOptions?.records ||
      previousData?.getConversationParticipantOptions?.records ||
      [],
    [
      data?.getConversationParticipantOptions?.records,
      previousData?.getConversationParticipantOptions?.records,
    ],
  );

  return {
    conversationParticipantOptions,
    loading,
    fetch: getConversationParticipantOptions,
  };
};

export const useConversationParticipantOptions = () => {
  const { conversationParticipantOptions, fetch, loading } =
    useGetConversationParticipantOptions();

  const options: PreparedConversationParticipantOption[] = useMemo(
    () =>
      uniqueBy(
        conversationParticipantOptions
          .map(getPreparedConversationParticipantOption)
          .sort((a, b) => a.participantEntity?.localeCompare(b.participantEntity)),
        (it) => it.email || it.id,
      ),
    [conversationParticipantOptions],
  );

  return {
    conversationParticipantOptions: options,
    getConversationParticipantOptions: fetch,
    loading,
  };
};

function getPreparedConversationParticipantOption(
  data: ConversationParticipantOption,
): PreparedConversationParticipantOption {
  return {
    id: data.id,
    value: data.participantIdentity,
    label: [data.firstName, data.lastName].join(' ').trim(),
    conversationParticipantId: data.conversationParticipantId,
    participantEntity: data.participantEntity,
    phoneNumber: data.phoneNumber,
    email: data.email,
    group: getPreparedConversationParticipantOptionGroup(data.participantEntity),
    entityPartnerId: data.entityPartnerId,
    avatarFileKey: data.avatarFileKey,
  };
}

export function getPreparedConversationParticipantOptionGroup(
  participantEntity: ConversationParticipantEntity,
): string {
  switch (participantEntity) {
    case ConversationParticipantEntity.Client:
      return 'Clients';
    case ConversationParticipantEntity.PlatformUser:
      return 'Agents';
    case ConversationParticipantEntity.Rsm:
      return 'RSM';
    default:
      return 'Unidentified';
  }
}
